/**
 * 部分更新ナビゲーションの多層防御ガード（harden-partial-navigation design.md H2 /
 * fix-partial-nav-missing-main-fallback design.md D2・D5）。
 *
 * htmx:beforeSwap の応答に差し替え対象（#lms-main）が含まれるかを判定する関数、
 * 全画面遷移フォールバックの要否そのものを判定する関数、含まれない場合の
 * フォールバック先 URL を決める関数、および #lms-main 喪失からの 1 回限りの回復
 * （main.ts の MutationObserver〔主経路〕と htmx:afterSettle〔副次経路〕の双方が呼ぶ）
 * の判定器を提供する（実装時の改訂・2026-08-04、FPN-BR-05 起因。回復ガードの主経路が
 * MutationObserver に変わったため「htmx:afterSettle で使う最終ガード」という記述を訂正。
 * 判定器自体の契約・実装は無変更）。
 * `hasMainRegion` / `shouldFallbackToFullNavigation` / `shouldFocusAfterSettle` は
 * 純粋関数（引数だけから結果が決まり副作用も無い）。`fallbackUrl` は
 * `xhr.responseURL` / `pathInfo` のいずれも得られない最終フォールバック分岐でのみ
 * ブラウザグローバルの可変状態 `window.location.href` を読むため、厳密には純粋ではない
 * （実装時の訂正・2026-07-29、typescript-reviewer Medium-4 指摘。DOM 要素の読み書きや
 * htmx への依存は無く、main.ts から分離できる条件は変わらない）。
 * `createMainRegionRecoveryGuard` は「回復は 1 回まで」という状態をクロージャに閉じ込めた
 * ファクトリで、ファクトリ自体は純粋（呼ぶたびに独立した状態を持つ関数を返す）。
 * この回復ガードは**シェル（layout/base.html）を使うページ限定**である。引数
 * `mainRegionExistedAtBoot` が false のとき、返る判定器は入力によらず常に false を返す
 * （fix-partial-nav-login-reload-loop design.md D1・D2。2026-08-06 追加。
 * #lms-main を恒久的に持たない /login 系ページで回復ガードが無限リロードループを
 * 起こした不具合への対処。詳細は同関数の JSDoc）。
 *
 * main.ts から分離してある理由（レビュー指摘対応で実測確認済み）:
 *   main.ts は `import htmx from "htmx.org"` を持つため、main.ts のどの export を
 *   import してもモジュール全体が評価され、htmx.org の import 時副作用
 *   （`document` / `location` への直接アクセス、ready() 経由の XPath ベース DOM 走査）が
 *   node --test（jsdom 併用でも）で `location is not defined` /
 *   `XPathEvaluator is not defined` 等のエラーで失敗することを確認した。
 *   これらの関数は DOM 要素にも htmx にも依存しないため、依存を持たないこのモジュールに
 *   切り出すことで、既存の node --test 基盤（ビルド設定変更なし）で直接テストできる
 *   ようにしてある（partial-nav-guard.test.ts 参照）。
 */

/** 部分更新の差し替え対象（base.html の <main id="lms-main">）の id */
export const MAIN_REGION_ID = "lms-main";

/**
 * 応答本文が差し替え対象を含むかの判定に使う文字列。
 * base.html が id="lms-main" を直書きしており、Thymeleaf も esbuild も書式を変えないため
 * 文字列一致で足りる（harden-partial-navigation design.md H2）。
 */
export const MAIN_REGION_MARKER = `id="${MAIN_REGION_ID}"`;

/**
 * 全画面遷移フォールバックの対象外とする応答ステータス
 * （fix-partial-nav-missing-main-fallback design.md D2 の注意書き）。
 *
 * main.ts の `htmx.config.responseHandling` が `{ code: "204", swap: false }` を指定して
 * おり、204 応答では htmx が差し替えを行わない＝ #lms-main を失うリスクが無い。一方 204 は
 * 本文が空で #lms-main を含まないため、ステータスで除外しないと 204 のたびに全画面遷移して
 * しまう。
 *
 * **main.ts の responseHandling を変更するときは、この配列も必ず併せて見直すこと。**
 * 205（Reset Content）や 304（Not Modified）を足してはならない: これらは responseHandling の
 * `[23]..` に一致して swap: true になるため、除外すると本文の無い応答で #lms-main が
 * 削除されるのを止められなくなる（06_error-handling.md §4 の真理値表）。
 */
const FALLBACK_EXEMPT_STATUSES: readonly number[] = [204];

/**
 * fallbackUrl が参照する htmx:beforeSwap の detail のうち必要な部分だけを抜き出した形。
 * main.ts の HtmxSwapDetail はこれを構造的に満たす（余分なフィールドを持つだけ）。
 */
export interface FallbackUrlDetail {
  xhr?: { responseURL?: string };
  pathInfo?: {
    finalRequestPath: string;
    responsePath: string | null;
  };
}

/**
 * shouldFallbackToFullNavigation が参照する htmx:beforeSwap の detail の部分形
 * （fix-partial-nav-missing-main-fallback design.md D2）。
 */
export interface FallbackDecisionDetail {
  /** hx-boost 由来のリクエストなら true（htmx v2 の responseInfo.boosted） */
  boosted?: boolean | undefined;
  /** 応答の HTTP ステータス（xhr.status）。xhr を取得できない場合は undefined */
  status?: number | undefined;
  /** 応答本文（xhr.response）。文字列以外になりうるため unknown で受ける */
  serverResponse?: unknown;
}

/**
 * 応答本文に差し替え対象（#lms-main）が含まれるかを判定する。
 *
 * DOMParser による厳密検査はせず文字列一致で判定する（design.md H2）。誤って「含まない」と
 * 判定しても全画面遷移になるだけで表示は正しく、機能劣化がないため過剰な厳密さは要らない。
 * 文字列でない応答（responseType が変わった場合など）は「含まない」として安全側に倒す。
 */
export function hasMainRegion(serverResponse: unknown): boolean {
  return typeof serverResponse === "string" && serverResponse.includes(MAIN_REGION_MARKER);
}

/**
 * boosted 遷移の応答に対し、部分更新を中止して全画面遷移へフォールバックすべきかを判定する
 * （fix-partial-nav-missing-main-fallback design.md D2）。
 *
 * **htmx 内部の swap 可否判定（htmx:beforeSwap の detail.shouldSwap）を条件に含めない。**
 * 含めると、htmx が先に部分更新を取り止めた経路でフォールバックが発火せず、URL だけ更新されて
 * 本文が空白・シェルだけ残る白紙化（harden-partial-navigation の実機確認 HPN-BR-12）を
 * 止められない。確定仕様も「hx-select が応答に一致したかどうか、および htmx 内部の swap
 * 可否判定の結果にかかわらず発動しなければならない」と定めている。
 *
 * 判定は 3 段階:
 *   1. boosted でなければフォールバックしない（画面遷移の代替でない htmx リクエストは対象外）
 *   2. FALLBACK_EXEMPT_STATUSES（204）ならフォールバックしない
 *   3. 応答本文に #lms-main が無ければフォールバックする
 */
export function shouldFallbackToFullNavigation(detail: FallbackDecisionDetail): boolean {
  if (detail.boosted !== true) {
    return false;
  }
  if (detail.status !== undefined && FALLBACK_EXEMPT_STATUSES.includes(detail.status)) {
    return false;
  }
  return !hasMainRegion(detail.serverResponse);
}

/**
 * #lms-main を含まない応答を全画面遷移で開き直すときの遷移先 URL を決める（design.md H2）。
 *
 * 優先順位は次の 4 段階（06_error-handling.md §4 B-2）:
 *   1. リダイレクトを追った最終 URL（`xhr.responseURL`）
 *   2. 取得できない場合（CORS 等で空文字列になる場合を含む）は `pathInfo.responsePath`
 *   3. それも無ければ `pathInfo.finalRequestPath`
 *   4. `pathInfo` 自体が無い場合の最終防衛線として、現在のページ URL（`window.location.href`）
 *
 * この最終防衛線（4）が本関数を非純粋にしている唯一の箇所である（実装時の訂正・2026-07-29、
 * typescript-reviewer Medium-4 指摘。ファイル冒頭のコメント参照）。
 */
export function fallbackUrl(detail: FallbackUrlDetail): string {
  const responseUrl = detail.xhr?.responseURL;
  if (responseUrl) {
    return responseUrl;
  }
  const pathInfo = detail.pathInfo;
  return pathInfo?.responsePath ?? pathInfo?.finalRequestPath ?? window.location.href;
}

/**
 * shouldFocusAfterSettle が参照する htmx:afterSettle の detail のうち必要な部分だけを抜き出した形
 * （apply-uiux-review-wave1 / 30_htmx-feedback.md D30-5）。
 */
export interface FocusAfterSettleDetail {
  /** hx-boost によるページ遷移なら true（htmx v2 の responseInfo.boosted） */
  boosted?: boolean | undefined;
  /** 差し替え対象の id（outerHTML 差し替え後は DOM から外れた旧要素の id を含む） */
  targetId?: string | undefined;
}

/**
 * 部分更新後、新しいページの見出しへフォーカスを移してよいかを判定する（受講者 #4）。
 *
 * 絞り込みは boosted と targetId の 2 条件（design.md D30-5）。boosted だけだと将来 hx-boost を
 * 他領域に広げたときに巻き込み、targetId だけだと boosted 以外の htmx リクエスト（hx-get を
 * #lms-main に向けた場合）で誤発火する。両方を満たすときのみフォーカスを移してよいと判定する。
 * DOM 要素にも htmx にも依存しない純粋関数のため main.ts から分離し、node --test で検証できる
 * ようにしてある（partial-nav-guard.test.ts 参照）。
 */
export function shouldFocusAfterSettle(detail: FocusAfterSettleDetail): boolean {
  return detail.boosted === true && detail.targetId === MAIN_REGION_ID;
}

/**
 * 部分更新の完了後に #lms-main が DOM から失われていた場合の、回復ガードの判定器を作る
 * （fix-partial-nav-missing-main-fallback design.md D5。main.ts の MutationObserver
 * 〔主経路〕と htmx:afterSettle〔副次経路〕の双方がこの判定器を呼ぶ。実装時の改訂・
 * 2026-08-04、FPN-BR-05 起因で「最終ガードの判定器」という記述を訂正。判定器自体の
 * 契約・実装は無変更）。
 *
 * 返す関数に「差し替え対象が DOM に存在するか」を渡すと、全画面遷移で回復すべきかを返す。
 * 回復を許可するのは 1 回だけで、2 回目以降は常に false を返す（回復遷移がまた #lms-main の
 * 無いページを返す場合に無限リロードへ陥らせないため）。回復遷移が成功すればページごと
 * 再読み込みされ、このクロージャの状態自体が消えるので正常時は自然にリセットされる。
 *
 * **前提条件: 回復ガードはシェル（layout/base.html）を使うページ限定である**
 * （fix-partial-nav-login-reload-loop design.md D1・D2。2026-08-06 追加）。
 *   このガードが守る不変条件は「#lms-main は常に DOM に存在する」だが、この不変条件が
 *   成立するのはシェル付きページだけである。/login 系（/login・/login?logout・
 *   /login?error・/login/initial-password）はシェルを使わない独立テンプレートで
 *   #lms-main を恒久的に持たないため、守るべき不変条件が最初から無く、回復（同一 URL への
 *   全画面遷移）は原理的に無意味である。
 *   そこで引数 mainRegionExistedAtBoot（ページ読み込み時点で #lms-main が DOM に存在したか）
 *   が false のとき、返る判定器は入力によらず常に false を返す。前提が false のときは
 *   一回性フラグ（recoveryStarted）を消費しないため、何度呼ばれても挙動は変わらない。
 *
 *   この前提条件を入れていなかった旧実装では、GET /login?logout が毎回描画するフラッシュ
 *   メッセージを initFlashToasts がトースト化する際の DOM 変異（[data-lms-flash] 要素の
 *   remove()）で main.ts の MutationObserver が発火し、#lms-main が無いと判定して
 *   同一 URL へ全画面遷移 → 新しい JS コンテキストで一回性フラグがリセット → 同条件が再現、
 *   という無限リロードループが起きた（AWS 本番の動作チェック 2026-08-06 で発見・再現率 2/2。
 *   ログアウトした利用者が再ログインのフォーム入力すらできなくなる。確定仕様
 *   partial-navigation「この回復遷移が無限に繰り返されてはならない（MUST NOT）」への違反）。
 *
 *   前提条件を main.ts 側の if ではなくこの判定器に持たせているのは、主経路
 *   （MutationObserver）と副次経路（htmx:afterSettle）が同一インスタンスを共有しており、
 *   1 箇所の判定で両経路を同時に無効化できるためである（呼び出し箇所ごとに if を書くと
 *   将来どちらか一方だけが直され乖離する）。main.ts 側の observer 登録スキップは
 *   「シェルなしページで全 DOM 変異を監視し続ける無駄をなくす」ための最適化であり、
 *   正しさの担保はこの判定器側にある（二層の防御。どちらも削ってはならない）。
 *
 * 状態をモジュールスコープの変数ではなくクロージャに閉じ込めているのは、テストごとに独立した
 * インスタンスを作れるようにするため（partial-nav-guard.test.ts の FPN-UT-11〜15 が一回性を、
 * FLR-UT-01〜06 が前提条件そのものを検証する）。initPartialNavigation() は boot() から
 * 1 回しか呼ばれないため、実行時の意味は design.md D5 の「モジュール内フラグ」と等価である。
 *
 * **このガードは shouldFallbackToFullNavigation が正しく動いていても削ってはならない**
 * （design.md D5。CLAUDE.md の多層防御方針——@NotBlank があっても strip() 後の空判定を
 * 削らない——と同型である）。
 *
 * @param mainRegionExistedAtBoot ページ読み込み時点（initPartialNavigation() の実行時）に
 *   #lms-main が DOM に存在したか。false ならこの判定器は入力によらず常に false を返す。
 *   **既定値は与えない**（省略可能にすると、呼び出し忘れが型エラーにならずに回復ガードを
 *   有効化してしまい、本 change が塞いだ事故が黙って戻るため）
 */
export function createMainRegionRecoveryGuard(
  mainRegionExistedAtBoot: boolean,
): (mainRegionExists: boolean) => boolean {
  let recoveryStarted = false;
  return function shouldRecoverMainRegion(mainRegionExists: boolean): boolean {
    if (!mainRegionExistedAtBoot || mainRegionExists || recoveryStarted) {
      return false;
    }
    recoveryStarted = true;
    return true;
  };
}
